import * as Localization from 'expo-localization'
import AsyncStorage from '@react-native-async-storage/async-storage'
import { LanguageDetectorAsyncModule } from 'i18next'

import { showAlert } from '../utils/alert'
import { getStoredIsAuthenticated } from '../utils/auth-session-storage'
import { getScopedStorageKey } from '../utils/storage-scope'

const STORE_LANGUAGE_KEY = 'settings.lang'
const STORE_LANGUAGE_USER_SELECTED_KEY = 'settings.lang.userSelected'
const SUPPORTED_LANGUAGES = ['en', 'sv'] as const

type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number]

const normalizeLanguage = (
  language: string | null | undefined,
): SupportedLanguage | null => {
  if (typeof language !== 'string') return null
  const normalizedLanguage = language.toLowerCase().split('-')[0]
  return SUPPORTED_LANGUAGES.includes(normalizedLanguage as SupportedLanguage)
    ? (normalizedLanguage as SupportedLanguage)
    : null
}

const getDeviceLanguage = (): SupportedLanguage => {
  const locales = Localization.getLocales()
  const deviceLanguage = locales?.[0]?.languageCode
  return normalizeLanguage(deviceLanguage) ?? 'en'
}

export const getLanguageForAuthState = async (
  isAuthenticated: boolean,
): Promise<SupportedLanguage> => {
  if (!isAuthenticated) {
    return getDeviceLanguage()
  }

  const [languageKey, userSelectedKey] = await Promise.all([
    getScopedStorageKey(STORE_LANGUAGE_KEY, { requireUserScope: true }),
    getScopedStorageKey(STORE_LANGUAGE_USER_SELECTED_KEY, {
      requireUserScope: true,
    }),
  ])

  if (languageKey == null || userSelectedKey == null) {
    return getDeviceLanguage()
  }

  const [[, storedLanguage], [, isUserSelectedLanguage]] =
    await AsyncStorage.multiGet([languageKey, userSelectedKey])

  if (isUserSelectedLanguage === '1') {
    const normalizedStoredLanguage = normalizeLanguage(storedLanguage)
    if (normalizedStoredLanguage) {
      return normalizedStoredLanguage
    }
  }

  return getDeviceLanguage()
}

export const setUserSelectedLanguage = async (
  language: SupportedLanguage,
): Promise<void> => {
  if (typeof window === 'undefined') return

  const [languageKey, userSelectedKey] = await Promise.all([
    getScopedStorageKey(STORE_LANGUAGE_KEY),
    getScopedStorageKey(STORE_LANGUAGE_USER_SELECTED_KEY),
  ])

  if (languageKey == null || userSelectedKey == null) {
    return
  }

  await AsyncStorage.multiSet([
    [languageKey, language],
    [userSelectedKey, '1'],
  ])
}

const languageDetector: LanguageDetectorAsyncModule = {
  type: 'languageDetector',
  async: true,
  init: () => {},
  detect: async () => {
    // Prevent execution during static generation (Node environment)
    if (typeof window === 'undefined') {
      return 'en'
    }

    try {
      const isAuthenticated = await getStoredIsAuthenticated()
      return await getLanguageForAuthState(isAuthenticated)
    } catch {
      return 'en'
    }
  },
  cacheUserLanguage: async (language) => {
    if (typeof window === 'undefined') {
      return
    }

    try {
      const userSelectedKey = await getScopedStorageKey(
        STORE_LANGUAGE_USER_SELECTED_KEY,
        { requireUserScope: true },
      )
      const languageKey = await getScopedStorageKey(STORE_LANGUAGE_KEY, {
        requireUserScope: true,
      })

      if (userSelectedKey == null || languageKey == null) {
        return
      }

      const [isUserSelectedLanguage, isAuthenticated] = await Promise.all([
        AsyncStorage.getItem(userSelectedKey),
        getStoredIsAuthenticated(),
      ])

      if (isUserSelectedLanguage !== '1' || !isAuthenticated) return

      const normalizedLanguage = normalizeLanguage(language) ?? 'en'
      await AsyncStorage.setItem(languageKey, normalizedLanguage)
    } catch {
      try {
        const i18n = (await import('../i18n')).default
        showAlert(
          i18n.t('common:error'),
          i18n.t('common:failed_to_save_language'),
        )
      } catch {
        showAlert('Error', 'Failed to save language preference.')
      }
    }
  },
}

export default languageDetector
